// ══════════════════════════════════════════════════════════════════════
// Scenario Planner (R7s-2) — What-If Analysis for Backtests
// Features: Re-simulation WITH compounding (not linear), SVG charts,
// Monte-Carlo bootstrap for Risk-of-Ruin confidence intervals.
// ══════════════════════════════════════════════════════════════════════

import { useState, useMemo, useEffect } from "react";
import { theme, fonts } from "../theme";
import { Card, CardHeader, Button, Pill, formatPnl } from "../components/ui";
import { useApi } from "../hooks/useApi";
import { getApi } from "./strategy-lab/constants";
import { toast } from "../components/shared";

/* ══════════════════════════════════════════════════════════════════════
   CORE SIMULATION — WITH Compounding (correct) vs WITHOUT (linear)
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Re-simulate trade stream under scenario parameters.
 * Assumes original backtest used fixed_fractional risk sizing.
 *
 * Input per trade: entry_time, exit_time, pnl (from original backtest)
 * Approximation: We estimate 1R = |avg_loss| to derive per-trade R-multiples.
 * Then scale to new risk_dollar and apply compounding.
 */
function resimulate(trades, opts) {
  const { accountSize, riskPct, commission, minRR } = opts;

  // Estimate original 1R from losers
  const losers = trades.filter(t => (t.pnl || 0) < 0);
  const origAvgLoss = losers.length > 0
    ? Math.abs(losers.reduce((s, t) => s + t.pnl, 0) / losers.length)
    : 100;

  // Compute per-trade R-multiple from original
  const withR = trades.map(t => ({
    ...t,
    _rr: origAvgLoss > 0 ? (t.pnl || 0) / origAvgLoss : 0,
  }));

  // Filter by minRR (only filter winners — losers always count)
  const filtered = withR.filter(t => t._rr <= 0 || Math.abs(t._rr) >= minRR);

  // Re-simulate with compounding
  let equity = accountSize;
  let peak = equity;
  let maxDD = 0;
  const equityCurve = [{ date: null, equity, dd: 0 }];
  const byMonth = {};

  const newTrades = filtered.map(t => {
    const riskDollar = equity * (riskPct / 100);
    const newPnl = t._rr * riskDollar - commission;
    equity += newPnl;
    if (equity > peak) peak = equity;
    const dd = peak - equity;
    if (dd > maxDD) maxDD = dd;

    // Monthly aggregation
    const date = t.exit_time || t.entry_time || "";
    const month = typeof date === "string" ? date.slice(0, 7) : "";
    if (month) byMonth[month] = (byMonth[month] || 0) + newPnl;

    equityCurve.push({ date, equity, dd });
    return { ...t, newPnl, newEquity: equity };
  });

  const finalPnl = equity - accountSize;
  const winners = newTrades.filter(t => t.newPnl > 0);
  const newLosers = newTrades.filter(t => t.newPnl < 0);
  const grossWin = winners.reduce((s, t) => s + t.newPnl, 0);
  const grossLoss = Math.abs(newLosers.reduce((s, t) => s + t.newPnl, 0));

  return {
    trades: newTrades,
    finalPnl,
    finalEquity: equity,
    maxDD,
    maxDDPct: accountSize > 0 ? (maxDD / accountSize) * 100 : 0,
    winRate: newTrades.length > 0 ? (winners.length / newTrades.length) * 100 : 0,
    pf: grossLoss > 0 ? grossWin / grossLoss : 0,
    expectancy: newTrades.length > 0 ? finalPnl / newTrades.length : 0,
    totalTrades: newTrades.length,
    winners: winners.length,
    losers: newLosers.length,
    equityCurve,
    byMonth,
    returnPct: accountSize > 0 ? (finalPnl / accountSize) * 100 : 0,
    avgWin: winners.length > 0 ? grossWin / winners.length : 0,
    avgLoss: newLosers.length > 0 ? grossLoss / newLosers.length : 0,
    origAvgLoss,
  };
}

/* ══════════════════════════════════════════════════════════════════════
   MONTE CARLO — Bootstrap trade order N times for confidence intervals
   ══════════════════════════════════════════════════════════════════════ */
function monteCarlo(trades, opts, N = 500) {
  const results = [];
  const random = (seed) => { const x = Math.sin(seed * 9999.1) * 10000; return x - Math.floor(x); };

  for (let i = 0; i < N; i++) {
    // Fisher-Yates shuffle with seeded random
    const shuffled = [...trades];
    for (let j = shuffled.length - 1; j > 0; j--) {
      const k = Math.floor(random(i * 1000 + j) * (j + 1));
      [shuffled[j], shuffled[k]] = [shuffled[k], shuffled[j]];
    }
    const sim = resimulate(shuffled, opts);
    results.push({
      finalPnl: sim.finalPnl,
      maxDD: sim.maxDD,
      maxDDPct: sim.maxDDPct,
      returnPct: sim.returnPct,
    });
  }

  results.sort((a, b) => a.finalPnl - b.finalPnl);
  const pct = (p) => results[Math.floor(p * N)];
  const ruined = results.filter(r => r.maxDD >= opts.accountSize * 0.5).length;

  return {
    p5: pct(0.05),
    p25: pct(0.25),
    median: pct(0.5),
    p75: pct(0.75),
    p95: pct(0.95),
    worst: results[0],
    best: results[results.length - 1],
    ruinRate: (ruined / N) * 100,
  };
}

/* ══════════════════════════════════════════════════════════════════════
   SVG CHARTS — Pure SVG, no external deps
   ══════════════════════════════════════════════════════════════════════ */

/* Equity Curve Overlay — Original vs Scenario */
function EquityOverlay({ originalCurve, scenarioCurve, width = 900, height = 260 }) {
  if (!originalCurve?.length || !scenarioCurve?.length) return null;
  const P = 40;

  const origValues = originalCurve.map(p => p.equity);
  const scenValues = scenarioCurve.map(p => p.equity);
  const all = [...origValues, ...scenValues];
  const minV = Math.min(...all);
  const maxV = Math.max(...all);
  const range = maxV - minV || 1;

  const toPoints = (curve) => curve.map((p, i) => {
    const x = P + (i / (curve.length - 1 || 1)) * (width - 2 * P);
    const y = height - P - ((p.equity - minV) / range) * (height - 2 * P);
    return [x, y];
  });

  const origPts = toPoints(originalCurve);
  const scenPts = toPoints(scenarioCurve);
  const origPath = origPts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ");
  const scenPath = scenPts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0]} ${p[1]}`).join(" ");

  // Fill below scenario curve
  const scenFill = `${scenPath} L ${scenPts[scenPts.length-1][0]} ${height - P} L ${scenPts[0][0]} ${height - P} Z`;

  return <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto" }}>
    {/* Grid */}
    {[0, 0.25, 0.5, 0.75, 1].map(t => {
      const y = height - P - t * (height - 2 * P);
      const v = minV + t * range;
      return <g key={t}>
        <line x1={P} y1={y} x2={width - P} y2={y} stroke={theme.divider} strokeWidth={1}/>
        <text x={4} y={y + 4} fontSize={9} fill={theme.text4} fontFamily={fonts.mono}>${Math.round(v/1000)}k</text>
      </g>;
    })}
    {/* Scenario fill */}
    <path d={scenFill} fill={theme.blue} fillOpacity={0.08}/>
    {/* Original curve */}
    <path d={origPath} fill="none" stroke={theme.text3} strokeWidth={1.5} strokeDasharray="4 4"/>
    {/* Scenario curve */}
    <path d={scenPath} fill="none" stroke={theme.blue} strokeWidth={2}/>
    {/* Legend */}
    <g transform={`translate(${width - 180}, 20)`}>
      <rect x={0} y={0} width={170} height={44} fill={theme.white} stroke={theme.divider} rx={4}/>
      <line x1={10} y1={14} x2={30} y2={14} stroke={theme.text3} strokeWidth={1.5} strokeDasharray="4 4"/>
      <text x={38} y={18} fontSize={10} fill={theme.text2} fontFamily={fonts.mono}>Original</text>
      <line x1={10} y1={32} x2={30} y2={32} stroke={theme.blue} strokeWidth={2}/>
      <text x={38} y={36} fontSize={10} fill={theme.text2} fontFamily={fonts.mono}>Scenario</text>
    </g>
  </svg>;
}

/* Drawdown Chart */
function DDChart({ curve, width = 900, height = 140 }) {
  if (!curve?.length) return null;
  const P = 40;
  const maxDD = Math.max(...curve.map(p => p.dd || 0), 1);
  const pts = curve.map((p, i) => {
    const x = P + (i / (curve.length - 1 || 1)) * (width - 2 * P);
    const y = P + ((p.dd || 0) / maxDD) * (height - 2 * P);
    return [x, y];
  });
  const path = `M ${P} ${P} ${pts.map(p => `L ${p[0]} ${p[1]}`).join(" ")} L ${width - P} ${P} Z`;
  return <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto" }}>
    <line x1={P} y1={P} x2={width - P} y2={P} stroke={theme.divider}/>
    <text x={4} y={P + 4} fontSize={9} fill={theme.text4} fontFamily={fonts.mono}>$0</text>
    <text x={4} y={height - P + 4} fontSize={9} fill={theme.text4} fontFamily={fonts.mono}>-${Math.round(maxDD/1000)}k</text>
    <path d={path} fill={theme.red} fillOpacity={0.15} stroke={theme.red} strokeWidth={1.5}/>
  </svg>;
}

/* Monthly Heatmap */
function MonthlyHeatmap({ byMonth }) {
  const entries = Object.entries(byMonth || {}).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) return <div style={{ fontSize: 11, color: theme.text4 }}>Keine monatlichen Daten</div>;
  const years = [...new Set(entries.map(([m]) => m.slice(0, 4)))].sort();
  const maxAbs = Math.max(...entries.map(([, v]) => Math.abs(v)), 1);

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

  return <div>
    <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 2, fontFamily: fonts.mono, fontSize: 10 }}>
      <thead><tr>
        <th style={{ textAlign: "left", padding: "4px 8px", color: theme.text4, fontSize: 9, fontWeight: 700 }}>Jahr</th>
        {monthNames.map(m => <th key={m} style={{ padding: "4px 2px", color: theme.text4, fontSize: 9, fontWeight: 700 }}>{m}</th>)}
        <th style={{ padding: "4px 6px", color: theme.text4, fontSize: 9, fontWeight: 700 }}>Summe</th>
      </tr></thead>
      <tbody>{years.map(y => {
        const yearTotal = entries.filter(([m]) => m.startsWith(y)).reduce((s, [, v]) => s + v, 0);
        return <tr key={y}>
          <td style={{ padding: "4px 8px", fontWeight: 700, color: theme.text2 }}>{y}</td>
          {monthNames.map((_, idx) => {
            const key = `${y}-${String(idx + 1).padStart(2, "0")}`;
            const v = byMonth[key];
            if (v == null) return <td key={idx} style={{ background: theme.tint }}/>;
            const intensity = Math.min(Math.abs(v) / maxAbs, 1);
            const bg = v >= 0
              ? `rgba(22,163,74,${0.15 + intensity * 0.5})`
              : `rgba(239,68,68,${0.15 + intensity * 0.5})`;
            return <td key={idx} title={`${key}: ${formatPnl(v)}`} style={{ padding: "5px 2px", background: bg, color: theme.text, fontSize: 9, fontWeight: 600, textAlign: "center", borderRadius: 3, cursor: "default" }}>
              {Math.abs(v) >= 1000 ? `${Math.round(v/1000)}k` : Math.round(v)}
            </td>;
          })}
          <td style={{ padding: "4px 6px", fontWeight: 700, color: yearTotal >= 0 ? theme.greenText : theme.redText }}>
            {yearTotal >= 0 ? "+" : ""}{Math.round(yearTotal/1000)}k
          </td>
        </tr>;
      })}</tbody>
    </table>
  </div>;
}

/* ══════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════════ */
export default function ScenarioPlanner() {
  const { data: btH } = useApi("/results/backtests", 0);
  const btRuns = Array.isArray(btH) ? btH : (btH?.runs || []);

  const [selectedRunId, setSelectedRunId] = useState(null);
  const [runData, setRunData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [runMC, setRunMC] = useState(false);
  const [mcResults, setMcResults] = useState(null);

  // Auto-pick latest non-auto-baseline backtest
  useEffect(() => {
    if (!selectedRunId && btRuns.length > 0) {
      const latest = btRuns.find(r => r.trigger !== "auto_baseline") || btRuns[0];
      if (latest?.id) setSelectedRunId(latest.id);
    }
  }, [btRuns, selectedRunId]);

  // Load full run data when selection changes
  useEffect(() => {
    if (!selectedRunId) return;
    setLoading(true);
    setMcResults(null);
    getApi(`/results/backtests/${selectedRunId}`)
      .then(d => { setRunData(d); setLoading(false); })
      .catch(e => { toast("Fehler beim Laden: " + e.message, "error"); setLoading(false); });
  }, [selectedRunId]);

  const trades = runData?.trades || [];
  const origPnl = runData?.net_pnl || 0;
  const origAccount = runData?.account_size || runData?.initial_capital || 50000;
  const origRisk = runData?.risk_pct || 0.4;
  const origCommission = runData?.commission || 0;

  // Scenario state
  const [accountSize, setAccountSize] = useState(50000);
  const [riskPct, setRiskPct] = useState(0.4);
  const [commission, setCommission] = useState(0);
  const [minRR, setMinRR] = useState(0);

  // Update defaults when new run loaded
  useEffect(() => {
    if (runData) {
      setAccountSize(origAccount);
      setRiskPct(origRisk);
      setCommission(origCommission);
      setMinRR(0);
    }
  }, [runData]);

  // Original baseline resimulation (for equity overlay comparison)
  const originalSim = useMemo(() => {
    if (!trades.length) return null;
    return resimulate(trades, { accountSize: origAccount, riskPct: origRisk, commission: origCommission, minRR: 0 });
  }, [trades, origAccount, origRisk, origCommission]);

  // Current scenario resimulation
  const scenarioSim = useMemo(() => {
    if (!trades.length) return null;
    return resimulate(trades, { accountSize, riskPct, commission, minRR });
  }, [trades, accountSize, riskPct, commission, minRR]);

  const runMonteCarlo = async () => {
    if (!trades.length) return;
    setRunMC(true);
    await new Promise(r => setTimeout(r, 50));  // yield to paint
    const mc = monteCarlo(trades, { accountSize, riskPct, commission, minRR }, 500);
    setMcResults(mc);
    setRunMC(false);
  };

  const reset = () => {
    setAccountSize(origAccount);
    setRiskPct(origRisk);
    setCommission(origCommission);
    setMinRR(0);
    setMcResults(null);
  };

  // Early states
  if (btRuns.length === 0) {
    return <Card style={{ padding: "40px 32px", textAlign: "center" }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginBottom: 6 }}>Kein Backtest vorhanden</div>
      <div style={{ fontSize: 12, color: theme.text3 }}>Führe zuerst im Strategy Lab einen Backtest durch.</div>
    </Card>;
  }

  const selectedRun = btRuns.find(r => r.id === selectedRunId);

  return <div>
    {/* Info header */}
    <Card style={{ marginBottom: 16, padding: "14px 18px", background: "rgba(37,99,235,0.03)", border: `1px solid rgba(37,99,235,0.15)` }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: theme.text, marginBottom: 6 }}>🎯 What-If-Analyse mit Re-Simulation</div>
      <div style={{ fontSize: 12, color: theme.text2, lineHeight: 1.6 }}>
        Spiele verschiedene <strong>Account-Größen</strong>, <strong>Risk-Einstellungen</strong>, <strong>Commission-Modelle</strong> und <strong>RR-Filter</strong> durch.
        Die Berechnung ist <strong>mit Compounding</strong> (Risk wächst mit Equity) — realistisch für Live-Trading.
      </div>
    </Card>

    {/* Backtest Selector */}
    <Card style={{ marginBottom: 16, padding: "16px 20px" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: theme.text, marginBottom: 10 }}>1. Backtest auswählen</div>
      <select
        value={selectedRunId || ""}
        onChange={e => setSelectedRunId(Number(e.target.value))}
        style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: `1px solid ${theme.border}`, fontSize: 13, fontFamily: fonts.mono, background: theme.white, cursor: "pointer" }}>
        {btRuns.filter(r => r.trigger !== "auto_baseline").map(r =>
          <option key={r.id} value={r.id}>
            #{r.id} · {r.instrument} · {formatPnl(r.net_pnl)} · {r.total_trades} trades · {r.entry_tf || "?"}/{r.context_tf || "?"}/{r.regime_tf || "?"} · {(r.timestamp || "").slice(0, 16).replace("T", " ")}
          </option>
        )}
      </select>
      {selectedRun && <div style={{ marginTop: 10, fontSize: 11, color: theme.text3, fontFamily: fonts.mono, lineHeight: 1.5 }}>
        {selectedRun.strategy_file} · Zeitraum: {selectedRun.start_date?.slice(0,10)} → {selectedRun.end_date?.slice(0,10)}
        {originalSim && <span> · {trades.length} Trades · 1R ≈ ${originalSim.origAvgLoss.toFixed(0)}</span>}
      </div>}
    </Card>

    {loading && <Card style={{ padding: "60px", textAlign: "center" }}>
      <div style={{ fontSize: 26, marginBottom: 10 }}>⏳</div>
      <div style={{ fontSize: 13, color: theme.text2 }}>Lade Trades…</div>
    </Card>}

    {!loading && runData && trades.length === 0 && <Card style={{ padding: "40px", textAlign: "center" }}>
      <div style={{ fontSize: 13, color: theme.amberText }}>⚠️ Dieser Backtest hat keine Trades gespeichert. Wähle einen anderen.</div>
    </Card>}

    {!loading && scenarioSim && <>

      {/* Sliders */}
      <Card style={{ marginBottom: 16, padding: "18px 22px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: theme.text }}>2. Parameter anpassen</div>
          <Button onClick={reset} style={{ fontSize: 11, padding: "5px 11px" }}>Reset auf Original</Button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 22 }}>
          {/* Account Size */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: theme.text2, textTransform: "uppercase", letterSpacing: "0.5px" }}>💰 Account Size</label>
              <span style={{ fontSize: 15, fontWeight: 800, fontFamily: fonts.mono, color: theme.blueText }}>${accountSize.toLocaleString()}</span>
            </div>
            <input type="range" min={5000} max={500000} step={1000} value={accountSize} onChange={e => setAccountSize(Number(e.target.value))} style={{ width: "100%", accentColor: theme.blue }}/>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: theme.text4, fontFamily: fonts.mono, marginTop: 3 }}>
              <span>$5k</span><span>$500k</span>
            </div>
          </div>
          {/* Risk % */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: theme.text2, textTransform: "uppercase", letterSpacing: "0.5px" }}>🎲 Risk pro Trade</label>
              <span style={{ fontSize: 15, fontWeight: 800, fontFamily: fonts.mono, color: theme.blueText }}>{riskPct.toFixed(2)}%</span>
            </div>
            <input type="range" min={0.05} max={2.0} step={0.05} value={riskPct} onChange={e => setRiskPct(Number(e.target.value))} style={{ width: "100%", accentColor: theme.blue }}/>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: theme.text4, fontFamily: fonts.mono, marginTop: 3 }}>
              <span>0.05%</span><span>2.0%</span>
            </div>
            <div style={{ fontSize: 10, color: theme.text3, marginTop: 4, fontFamily: fonts.mono }}>Start-Risk: ${(accountSize * riskPct / 100).toFixed(0)} pro Trade</div>
          </div>
          {/* Commission */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: theme.text2, textTransform: "uppercase", letterSpacing: "0.5px" }}>💳 Commission/Trade</label>
              <span style={{ fontSize: 15, fontWeight: 800, fontFamily: fonts.mono, color: theme.blueText }}>${commission.toFixed(2)}</span>
            </div>
            <input type="range" min={0} max={20} step={0.25} value={commission} onChange={e => setCommission(Number(e.target.value))} style={{ width: "100%", accentColor: theme.blue }}/>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: theme.text4, fontFamily: fonts.mono, marginTop: 3 }}>
              <span>$0</span><span>$20</span>
            </div>
            <div style={{ fontSize: 10, color: theme.text3, marginTop: 4, fontFamily: fonts.mono }}>IBKR MGC ~$1-2 · Topstep ~$2-4</div>
          </div>
          {/* Min RR */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: theme.text2, textTransform: "uppercase", letterSpacing: "0.5px" }}>🎯 Min-RR der Winners</label>
              <span style={{ fontSize: 15, fontWeight: 800, fontFamily: fonts.mono, color: theme.blueText }}>{minRR.toFixed(1)}R</span>
            </div>
            <input type="range" min={0} max={5} step={0.25} value={minRR} onChange={e => setMinRR(Number(e.target.value))} style={{ width: "100%", accentColor: theme.blue }}/>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: theme.text4, fontFamily: fonts.mono, marginTop: 3 }}>
              <span>alle</span><span>nur ≥5R</span>
            </div>
            <div style={{ fontSize: 10, color: theme.text3, marginTop: 4, fontFamily: fonts.mono }}>Filtert {trades.length - scenarioSim.totalTrades} Winner &lt; {minRR}R</div>
          </div>
        </div>
      </Card>

      {/* Headline Metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 14 }}>
        <Card style={{ padding: "14px 16px", border: `2px solid ${scenarioSim.finalPnl >= 0 ? theme.green : theme.red}`, background: scenarioSim.finalPnl >= 0 ? "rgba(22,163,74,0.02)" : "rgba(239,68,68,0.02)" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 5 }}>Scenario Net P&L</div>
          <div style={{ fontSize: 22, fontWeight: 900, fontFamily: fonts.mono, color: scenarioSim.finalPnl >= 0 ? theme.greenText : theme.redText, lineHeight: 1 }}>{formatPnl(scenarioSim.finalPnl)}</div>
          <div style={{ fontSize: 10, fontFamily: fonts.mono, color: theme.text3, marginTop: 4 }}>
            Original: {formatPnl(origPnl)} · Δ {((scenarioSim.finalPnl - origPnl) / Math.abs(origPnl || 1) * 100).toFixed(1)}%
          </div>
        </Card>
        <Card style={{ padding: "14px 16px" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 5 }}>Max Drawdown</div>
          <div style={{ fontSize: 18, fontWeight: 800, fontFamily: fonts.mono, color: theme.redText, lineHeight: 1 }}>${scenarioSim.maxDD.toFixed(0)}</div>
          <div style={{ fontSize: 10, fontFamily: fonts.mono, color: scenarioSim.maxDDPct >= 50 ? theme.redText : scenarioSim.maxDDPct >= 25 ? theme.amberText : theme.greenText, fontWeight: 700, marginTop: 4 }}>
            {scenarioSim.maxDDPct >= 50 ? "💀" : scenarioSim.maxDDPct >= 25 ? "⚠️" : "✓"} {scenarioSim.maxDDPct.toFixed(1)}% vom Account
          </div>
        </Card>
        <Card style={{ padding: "14px 16px" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 5 }}>Final Equity</div>
          <div style={{ fontSize: 18, fontWeight: 800, fontFamily: fonts.mono, color: theme.text, lineHeight: 1 }}>${Math.round(scenarioSim.finalEquity).toLocaleString()}</div>
          <div style={{ fontSize: 10, fontFamily: fonts.mono, color: scenarioSim.returnPct >= 0 ? theme.greenText : theme.redText, marginTop: 4 }}>{scenarioSim.returnPct >= 0 ? "+" : ""}{scenarioSim.returnPct.toFixed(1)}% Return</div>
        </Card>
        <Card style={{ padding: "14px 16px" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 5 }}>Win Rate</div>
          <div style={{ fontSize: 18, fontWeight: 800, fontFamily: fonts.mono, color: theme.text, lineHeight: 1 }}>{scenarioSim.winRate.toFixed(1)}%</div>
          <div style={{ fontSize: 10, fontFamily: fonts.mono, color: theme.text3, marginTop: 4 }}>{scenarioSim.winners}W / {scenarioSim.losers}L</div>
        </Card>
        <Card style={{ padding: "14px 16px" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 5 }}>Profit Factor</div>
          <div style={{ fontSize: 18, fontWeight: 800, fontFamily: fonts.mono, color: scenarioSim.pf >= 1.3 ? theme.greenText : scenarioSim.pf >= 1 ? theme.text : theme.redText, lineHeight: 1 }}>{scenarioSim.pf.toFixed(2)}</div>
          <div style={{ fontSize: 10, fontFamily: fonts.mono, color: theme.text3, marginTop: 4 }}>{scenarioSim.pf >= 1.5 ? "solide" : scenarioSim.pf >= 1.2 ? "ok" : scenarioSim.pf >= 1 ? "borderline" : "⚠ negativ"}</div>
        </Card>
        <Card style={{ padding: "14px 16px" }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: ".6px", marginBottom: 5 }}>Ø Win / Loss</div>
          <div style={{ fontSize: 13, fontWeight: 800, fontFamily: fonts.mono, color: theme.text, lineHeight: 1.2 }}>${Math.round(scenarioSim.avgWin)} / ${Math.round(scenarioSim.avgLoss)}</div>
          <div style={{ fontSize: 10, fontFamily: fonts.mono, color: theme.text3, marginTop: 4 }}>Expectancy: ${Math.round(scenarioSim.expectancy)}/Trade</div>
        </Card>
      </div>

      {/* Charts */}
      <Card style={{ marginBottom: 14, padding: "14px 20px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: theme.text, marginBottom: 10 }}>📈 Equity-Kurve — Original vs Scenario</div>
        <EquityOverlay originalCurve={originalSim?.equityCurve} scenarioCurve={scenarioSim.equityCurve}/>
        <div style={{ fontSize: 10, color: theme.text3, marginTop: 6, fontFamily: fonts.mono }}>
          Original startet mit ${origAccount.toLocaleString()}, endet bei ${Math.round(originalSim?.finalEquity || 0).toLocaleString()} · Scenario endet bei ${Math.round(scenarioSim.finalEquity).toLocaleString()}
        </div>
      </Card>

      <Card style={{ marginBottom: 14, padding: "14px 20px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: theme.text, marginBottom: 10 }}>📉 Drawdown-Verlauf (Scenario)</div>
        <DDChart curve={scenarioSim.equityCurve}/>
      </Card>

      <Card style={{ marginBottom: 14, padding: "14px 20px" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: theme.text, marginBottom: 10 }}>📅 Monatliche Returns (Scenario)</div>
        <MonthlyHeatmap byMonth={scenarioSim.byMonth}/>
      </Card>

      {/* Monte Carlo */}
      <Card style={{ marginBottom: 14, padding: "16px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: theme.text, marginBottom: 3 }}>🎲 Monte-Carlo: Reihenfolge-Sensitivität</div>
            <div style={{ fontSize: 11, color: theme.text3 }}>Shuffled die Trade-Reihenfolge 500× und zeigt die Bandbreite möglicher Ergebnisse.</div>
          </div>
          <Button primary onClick={runMonteCarlo} disabled={runMC} style={{ fontSize: 12, padding: "8px 16px" }}>{runMC ? "Rechne…" : "Monte-Carlo starten"}</Button>
        </div>
        {mcResults && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginTop: 14 }}>
          <Card style={{ padding: "10px 14px", background: theme.redLight }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase" }}>Worst Case (P5)</div>
            <div style={{ fontSize: 14, fontWeight: 800, fontFamily: fonts.mono, color: theme.redText }}>{formatPnl(mcResults.p5.finalPnl)}</div>
            <div style={{ fontSize: 9, color: theme.text3, fontFamily: fonts.mono }}>DD: ${Math.round(mcResults.p5.maxDD)}</div>
          </Card>
          <Card style={{ padding: "10px 14px" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase" }}>Lower Quartile (P25)</div>
            <div style={{ fontSize: 14, fontWeight: 800, fontFamily: fonts.mono }}>{formatPnl(mcResults.p25.finalPnl)}</div>
            <div style={{ fontSize: 9, color: theme.text3, fontFamily: fonts.mono }}>DD: ${Math.round(mcResults.p25.maxDD)}</div>
          </Card>
          <Card style={{ padding: "10px 14px", background: theme.blueLight }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase" }}>Median (P50)</div>
            <div style={{ fontSize: 14, fontWeight: 800, fontFamily: fonts.mono, color: theme.blueText }}>{formatPnl(mcResults.median.finalPnl)}</div>
            <div style={{ fontSize: 9, color: theme.text3, fontFamily: fonts.mono }}>DD: ${Math.round(mcResults.median.maxDD)}</div>
          </Card>
          <Card style={{ padding: "10px 14px" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase" }}>Upper Quartile (P75)</div>
            <div style={{ fontSize: 14, fontWeight: 800, fontFamily: fonts.mono }}>{formatPnl(mcResults.p75.finalPnl)}</div>
            <div style={{ fontSize: 9, color: theme.text3, fontFamily: fonts.mono }}>DD: ${Math.round(mcResults.p75.maxDD)}</div>
          </Card>
          <Card style={{ padding: "10px 14px", background: theme.greenLight }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase" }}>Best Case (P95)</div>
            <div style={{ fontSize: 14, fontWeight: 800, fontFamily: fonts.mono, color: theme.greenText }}>{formatPnl(mcResults.p95.finalPnl)}</div>
            <div style={{ fontSize: 9, color: theme.text3, fontFamily: fonts.mono }}>DD: ${Math.round(mcResults.p95.maxDD)}</div>
          </Card>
          <Card style={{ padding: "10px 14px", border: mcResults.ruinRate > 5 ? `2px solid ${theme.red}` : `1px solid ${theme.divider}` }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: theme.text4, textTransform: "uppercase" }}>Risk of Ruin</div>
            <div style={{ fontSize: 14, fontWeight: 800, fontFamily: fonts.mono, color: mcResults.ruinRate > 5 ? theme.redText : mcResults.ruinRate > 1 ? theme.amberText : theme.greenText }}>{mcResults.ruinRate.toFixed(1)}%</div>
            <div style={{ fontSize: 9, color: theme.text3, fontFamily: fonts.mono }}>DD ≥ 50% vom Account</div>
          </Card>
        </div>}
        {mcResults && <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, background: theme.tint, fontSize: 11, color: theme.text2, lineHeight: 1.6 }}>
          <strong>💡 Interpretation:</strong> Die Spanne P5 bis P95 zeigt in welchem Bereich 90% der möglichen Ergebnisse liegen — je enger, desto zuverlässiger.
          Eine große Lücke zwischen Median und Worst-Case (P5) bedeutet: die Strategy hängt stark von Glück bei Trade-Reihenfolge ab.
          {mcResults.ruinRate > 5 && <span style={{ color: theme.redText, fontWeight: 700 }}> Risk-of-Ruin &gt; 5% ist ein klares Stop-Signal — reduziere Risk% oder erhöhe Account.</span>}
          {mcResults.ruinRate > 0 && mcResults.ruinRate <= 5 && <span style={{ color: theme.amberText, fontWeight: 700 }}> Risk-of-Ruin zwischen 1-5% ist Warn-Zone.</span>}
          {mcResults.ruinRate === 0 && <span style={{ color: theme.greenText, fontWeight: 700 }}> Risk-of-Ruin bei 0% — solide Risk-Konfig.</span>}
        </div>}
      </Card>

      {/* Compounding Note */}
      <Card style={{ padding: "12px 16px", background: "rgba(245,158,11,0.05)", border: `1px solid rgba(245,158,11,0.2)`, marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: theme.text2, lineHeight: 1.6 }}>
          <strong>📘 Methodik:</strong> Die Re-Simulation nutzt <strong>Compounding</strong> — Risk-Dollar wächst mit Equity, genau wie im Live-Bot.
          1R wird aus dem Ø-Loss der Original-Trades geschätzt (keine SL-Preise im BacktestResult gespeichert).
          Für präzise R-Multiples bräuchte es einen Backend-Change (SL-Distance pro Trade). Die Tendenz-Analyse ist aber solide.
        </div>
      </Card>
    </>}
  </div>;
}
